import './App.scss'
import { Route, default as Router } from './Router'
import ConsentManagement from './ConsentManagement'
import ErrorBoundary from './ErrorBoundary'
import Firewall from './Firewall'
import Footer from '../components/Layout/Footer'
import Header from '../components/Layout/Header'
import LazyComponent from '../components/LazyComponent'
import { Provider } from 'react-redux'
import React, { useEffect } from 'react'
import Toastr from './Toastr'
import UserLoader from './UserLoader'
import { isWebView } from './../utilities/displays'
import BottomNavbar from '../components/Layout/BottomNavbar'
import Wrapper from '../components/Layout/Wrapper'

// lazy load components per route in order to split the JS bundle in chunks
// @see https://serverless-stack.com/chapters/code-splitting-in-create-react-app.html
const Article = LazyComponent('Article')
const Biography = LazyComponent('Biography')
const BiographyList = LazyComponent('BiographyList')
const Bookmarks = LazyComponent('Bookmarks')
const CareerPage = LazyComponent('CareerPage')
const Category = LazyComponent('Category')
const Channel = LazyComponent('Channel')
const Contact = LazyComponent('Contact')
const CookieList = LazyComponent('CookieList')
const Homepage = LazyComponent('Homepage')
const Leadership = LazyComponent('Leadership')
const LiveRadio = LazyComponent('LiveRadio')
const LiveRadioWebView = LazyComponent('LiveRadioWebView')
const MarketingShow = LazyComponent('MarketingShow')
const NewsFeed = LazyComponent('NewsFeed')
const Newsletter = LazyComponent('Newsletter')
const PageNotFound = LazyComponent('PageNotFound')
const PrivacyPolicy = LazyComponent('PrivacyPolicy')
const Profile = LazyComponent('Profile')
const ResetPassword = LazyComponent('ResetPassword')
const Schedule = LazyComponent('Schedule')
const SpecialPage = LazyComponent('SpecialPage')
const SpecialPageWebView = LazyComponent('SpecialPageWebView')
const TermsOfService = LazyComponent('TermsOfService')
const AdvertisingTermsAndConditions = LazyComponent('AdvertisingTermsAndConditions')
const AccessibilityDeclaration = LazyComponent('AccessibilityDeclaration')
const PageError = LazyComponent('PageError')
const TagList = LazyComponent('TagList')
const Tag = LazyComponent('Tag')
const AuthorList = LazyComponent('AuthorList')
const Author = LazyComponent('Author')
const TopArticles = LazyComponent('TopArticles')
const ActivateAccount = LazyComponent('ActivateAccount')
const SignIn = LazyComponent('SignIn')
const SignUp = LazyComponent('SignUp')
const RequestResetPassword = LazyComponent('RequestResetPassword')
const Sitemap = LazyComponent('Sitemap')
const I24NewsPlus = LazyComponent('I24NewsPlus')

// AppTree :: () -> React.Component
const AppTree = () =>
  <UserLoader>
    <div className="app">
      <ErrorBoundary>
        <Toastr />
        <Header />
        <Wrapper>
          <ErrorBoundary context="route">
            <Route name="homepage" pattern={ /^\/((?<locale>(ar|en|fr|he|fa))\/?)?$/ } component={ Homepage } />
            <Route name="contact" pattern={ /^\/(?<locale>(ar|en|fr|he|fa)?)\/contact\/?$/ } component={ Contact } />
            <Route name="profile" pattern={ /^\/(?<locale>(ar|en|fr|he|fa)?)\/profile\/?$/ } component={ Firewall(Profile) } />
            <Route name="favorites" pattern={ /^\/(?<locale>(en|he|fa))\/favorites\/?$/ } component={ Firewall(Bookmarks) } />
            <Route name="favoritesAr" pattern={ /^\/(?<locale>ar)\/المفضلة\/?$/ } component={ Firewall(Bookmarks) } />
            <Route name="favoritesFr" pattern={ /^\/(?<locale>fr)\/favoris\/?$/ } component={ Firewall(Bookmarks) } />
            <Route name="termsOfService" pattern={ /^\/(?<locale>(en|he|fa))\/terms-of-service\/?$/ } component={ TermsOfService } />
            <Route name="termsOfServiceAr" pattern={ /^\/(?<locale>ar)\/شروط-الخدمة\/?$/ } component={ TermsOfService } />
            <Route name="termsOfServiceFr" pattern={ /^\/(?<locale>fr)\/conditions-generales-d-utilisation\/?$/ } component={ TermsOfService } />
            <Route name="privacyPolicy" pattern={ /^\/(?<locale>(en|he|fa))\/privacy-policy\/?$/ } component={ PrivacyPolicy } />
            <Route name="privacyPolicyAr" pattern={ /^\/(?<locale>ar)\/سياسة-خاصة\/?$/ } component={ PrivacyPolicy } />
            <Route name="privacyPolicyFr" pattern={ /^\/(?<locale>fr)\/politique-de-confidentialite\/?$/ } component={ PrivacyPolicy } />
            <Route name="advertisingTermsAndConditions" pattern={ /^\/(?<locale>(en|he|fa))\/advertising-terms-and-conditions\/?$/ } component={ AdvertisingTermsAndConditions } />
            <Route name="advertisingTermsAndConditionsFr" pattern={ /^\/(?<locale>fr)\/conditions-generales-publicitaire\/?$/ } component={ AdvertisingTermsAndConditions } />
            <Route name="advertisingTermsAndConditionsAr" pattern={ /^\/(?<locale>ar)\/شروط-وأحكام-الإعلان\/?$/ } component={ AdvertisingTermsAndConditions } />
            <Route name="accessibilityDeclaration" pattern={ /^\/(?<locale>(en|he|fa))\/accessibility-declaration\/?$/ } component={ AccessibilityDeclaration } />
            <Route name="accessibilityDeclarationFr" pattern={ /^\/(?<locale>fr)\/declaration-d-accessibilite\/?$/ } component={ AccessibilityDeclaration } />
            <Route name="accessibilityDeclarationAr" pattern={ /^\/(?<locale>ar)\/إعلان-إمكانية-الوصول\/?$/ } component={ AccessibilityDeclaration } />
            <Route name="careerPage" pattern={ /^\/(?<locale>(en|he|fa))\/career\/?$/ } component={ CareerPage } />
            <Route name="careerPageFr" pattern={ /^\/(?<locale>fr)\/carriere\/?$/ } component={ CareerPage } />
            <Route name="careerPageAr" pattern={ /^\/(?<locale>ar)\/حياة-مهنية\/?$/ } component={ CareerPage } />
            <Route name="requestResetPassword" pattern={ /^\/(?<locale>(en|he|fa))\/resetting\/request\/?$/ } component={ RequestResetPassword } />
            <Route name="requestResetPasswordFr" pattern={ /^\/(?<locale>fr)\/demande\/reinitialisation\/?$/ } component={ RequestResetPassword } />
            <Route name="requestResetPasswordAr" pattern={ /^\/(?<locale>ar)\/إعادة-تعيين\/طلب\/?$/ } component={ RequestResetPassword } />
            <Route name="resetPassword" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/reset-password\/(?<code>.*)\/?$/ } component={ ResetPassword } />
            <Route name="activateAccount" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/activate-account\/(?<code>.*)\/?$/ } component={ ActivateAccount } />
            <Route name="newsFeed" pattern={ /^\/(?<locale>(en|he|fa))\/news\/?$/ } component={ NewsFeed } />
            <Route name="newsFeedAr" pattern={ /^\/(?<locale>ar)\/آخر-الاخبار\/?$/ } component={ NewsFeed } />
            <Route name="newsFeedFr" pattern={ /^\/(?<locale>fr)\/fil-info\/?$/ } component={ NewsFeed } />
            <Route name="topArticles" pattern={ /^\/(?<locale>(en|he|fa))\/news\/more-news\/?$/ } component={ TopArticles } />
            <Route name="topArticlesAr" pattern={ /^\/(?<locale>ar)\/أخبار\/المزيد من الأخبار\/?$/ } component={ TopArticles } />
            <Route name="topArticlesFr" pattern={ /^\/(?<locale>fr)\/actu\/plus-d-actualites\/?$/ } component={ TopArticles } />
            <Route name="article" pattern={ /^\/(?<locale>(en|he|fa))\/news\/(?<rootCategory>[^/]+)\/((?<subCategory>[^/]+)\/)?(?<article>(([0-9]{10}|[0-9]{5,6}-[0-9]{6}|artc-)).*)\/?$/ } component={ Article } />
            <Route name="articleAr" pattern={ /^\/(?<locale>ar)\/أخبار\/(?<rootCategory>[^/]+)\/((?<subCategory>[^/]+)\/)?(?<article>(([0-9]{10}|[0-9]{5,6}-[0-9]{6}|artc-)).*)\/?$/ } component={ Article } />
            <Route name="articleFr" pattern={ /^\/(?<locale>fr)\/actu\/(?<rootCategory>[^/]+)\/((?<subCategory>[^/]+)\/)?(?<article>(([0-9]{10}|[0-9]{5,6}-[0-9]{6}|artc-)).*)\/?$/ } component={ Article } />
            <Route name="category" pattern={ /^\/(?<locale>(en|he|fa))\/news\/(?<rootCategory>[^/]+)(\/(?<subCategory>[^/]+))?\/?$/ } component={ Category } />
            <Route name="categoryAr" pattern={ /^\/(?<locale>ar)\/أخبار\/(?<rootCategory>[^/]+)(\/(?<subCategory>[^/]+))?\/?$/ } component={ Category } />
            <Route name="categoryFr" pattern={ /^\/(?<locale>fr)\/actu\/(?<rootCategory>[^/]+)(\/(?<subCategory>[^/]+))?\/?$/ } component={ Category } />
            <Route name="channel" pattern={ /^\/(?<locale>(en|he|fa))\/channels\/?$/ } component={ Channel } />
            <Route name="channelAr" pattern={ /^\/(?<locale>ar)\/القنوات\/?$/ } component={ Channel } />
            <Route name="channelFr" pattern={ /^\/(?<locale>fr)\/canaux\/?$/ } component={ Channel } />
            <Route name="schedules" pattern={ /^\/(?<locale>(en|he|fa))\/schedules\/(?<day>.*)\/?$/ } component={ Schedule } />
            <Route name="schedulesFr" pattern={ /^\/(?<locale>fr)\/grille-des-programmes\/(?<day>.*)\/?$/ } component={ Schedule } />
            <Route name="schedulesAr" pattern={ /^\/(?<locale>ar)\/جداول\/(?<day>.*)\/?$/ } component={ Schedule } />
            <Route name="shows" pattern={ /^\/(?<locale>(en|he|fa))\/shows\/?$/ } component={ MarketingShow } />
            <Route name="showsAr" pattern={ /^\/(?<locale>ar)\/برنامج\/?$/ } component={ MarketingShow } />
            <Route name="showsFr" pattern={ /^\/(?<locale>fr)\/emissions\/?$/ } component={ MarketingShow } />
            <Route name="leadership" pattern={ /^\/(?<locale>(en|he|fa))\/executive-committee\/?/ } component={ Leadership } />
            <Route name="leadershipAr" pattern={ /^\/(?<locale>ar)\/اللجنة-التنفيذية\/?/ } component={ Leadership } />
            <Route name="leadershipFr" pattern={ /^\/(?<locale>fr)\/comite-executif\/?/ } component={ Leadership } />
            <Route name="biographiesEn" pattern={ /^\/(?<locale>en)\/profiles\/?$/ } component={ BiographyList } />
            <Route name="biographiesFr" pattern={ /^\/(?<locale>fr)\/profils\/?$/ } component={ BiographyList } />
            <Route name="biographyEn" pattern={ /^\/(?<locale>en)\/profiles\/(?<reporter>[A-Za-z\\-]+.*?)\/?$/ } component={ Biography } />
            <Route name="biographyFr" pattern={ /^\/(?<locale>fr)\/profils\/(?<reporter>[A-Za-z\\-]+.*?)\/?$/ } component={ Biography } />
            <Route name="cookieList" pattern={ /^\/(?<locale>(en|he|fa))\/cookie-list\/?$/ } component={ CookieList } />
            <Route name="cookieListAr" pattern={ /^\/(?<locale>ar)\/بسكويت\/?$/ } component={ CookieList } />
            <Route name="cookieListFr" pattern={ /^\/(?<locale>fr)\/liste-des-cookies\/?$/ } component={ CookieList } />
            <Route name="liveRadio" pattern={ /^\/(?<locale>(en|he|fa))\/live-radio\/?$/ } component={ LiveRadio } />
            <Route name="liveRadioAr" pattern={ /^\/(?<locale>ar)\/راديو-مباشر\/?$/ } component={ LiveRadio }/>
            <Route name="liveRadioFr" pattern={ /^\/(?<locale>fr)\/en-direct\/?$/ } component={ LiveRadio } />
            <Route name="specialPage" pattern={ /^\/(?<locale>(en|fr|he|fa))\/special\/(?<slug>.*?)\/?$/ } component={ SpecialPage } />
            <Route name="specialPageAr" pattern={ /^\/(?<locale>ar)\/مميز\/(?<slug>.*?)\/?$/ } component={ SpecialPage } />
            <Route name="tags" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/tags\/?$/ } component={ TagList } />
            <Route name="groupIdentifierTags" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/tags\/(?<groupIdentifier>([a-z]|[\u0600-\u06ff]|[\u0750-\u077f]|[\ufb50-\ufbc1]|[\ufbd3-\ufd3f]|[\ufd50-\ufd8f]|[\ufd92-\ufdc7]|[\ufe70-\ufefc]|[\uFDF0-\uFDFD]|[\u0590-\u05fe]|special|مميز|ספיישל|ویژه))$/i } component={ TagList } />
            <Route name="tag" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/tags\/(?<tag>.*?)\/?$/ } component={ Tag } />
            <Route name="authors" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/authors\/?$/ } component={ AuthorList } />
            <Route name="author" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/authors\/(?<author>.*?)\/?$/ } component={ Author } />
            <Route name="newsletter" pattern={ /^\/(?<locale>(en|fr|he|fa))\/newsletter$/ } component={ Newsletter } />
            <Route name="newsletterAr" pattern={ /^\/(?<locale>ar)\/النشرة-الإخبارية$/ } component={ Newsletter } />
            <Route name="signIn" pattern={ /^\/(?<locale>(en|he|fa))\/sign-in\/?$/ } component={ SignIn } />
            <Route name="signInFr" pattern={ /^\/(?<locale>fr)\/connexion\/?$/ } component={ SignIn } />
            <Route name="signInAr" pattern={ /^\/(?<locale>ar)\/تسجيل-الدخول\/?$/ } component={ SignIn } />
            <Route name="signUp" pattern={ /^\/(?<locale>(en|he|fa))\/sign-up\/?$/ } component={ SignUp } />
            <Route name="signUpFr" pattern={ /^\/(?<locale>fr)\/inscription\/?$/ } component={ SignUp } />
            <Route name="signUpAr" pattern={ /^\/(?<locale>ar)\/التسجيل\/?$/ } component={ SignUp } />
            <Route name="sitemap" pattern={ /^\/(?<locale>(en|he|fa))\/sitemap$/ } component={ Sitemap } />
            <Route name="sitemapFr" pattern={ /^\/(?<locale>fr)\/plan-du-site$/ } component={ Sitemap } />
            <Route name="sitemapAr" pattern={ /^\/(?<locale>ar)\/خريطة-الموقع$/ } component={ Sitemap } />
            <Route name="i24NewsPlus" pattern={ /^\/(?<locale>(en|fr))\/i24news-plus\/?$/ } component={ I24NewsPlus } />
            <Route name="i24NewsPlusAr" pattern={ /^\/(?<locale>ar)\/بلس-i24news\/?$/ } component={ I24NewsPlus } />
            <Route name="pageError" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/(?<errorCode>\d{3})-(page|صفحة)$/ } component={ PageError } />
            <Route name="pageNotFound" pattern={ /^(\/(?<locale>(ar|en|fr|he|fa))\/?)?.*/ } component={ PageNotFound } />
          </ErrorBoundary>
        </Wrapper>
        <ConsentManagement />
        <BottomNavbar />
        <Footer />
      </ErrorBoundary>
    </div>
  </UserLoader>

// WebviewTree :: () -> React.Component
const WebviewTree = () =>
  <div className="app">
    <Wrapper>
      <ErrorBoundary context="route">
        <Route name="liveRadioWebView" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/live-shows\/?$/ } component={ LiveRadioWebView } />
        <Route name="specialWebView" pattern={ /^\/(?<locale>(ar|en|fr|he|fa))\/webview\/special\/?$/ } component={ SpecialPageWebView } />
        {/*
          * These routes are required so one can navigate from the special page
          * article list to article details
          */}
        <Route name="articleAr" pattern={ /^\/(?<locale>ar)\/أخبار\/(?<rootCategory>[^/]+)\/((?<subCategory>[^/]+)\/)?(?<article>([0-9]{10}|[0-9]{6}-[0-9]{6}|artc-).*)\/?$/ } component={ Article } />
        <Route name="articleFr" pattern={ /^\/(?<locale>fr)\/actu\/(?<rootCategory>[^/]+)\/((?<subCategory>[^/]+)\/)?(?<article>([0-9]{10}|[0-9]{6}-[0-9]{6}|artc-).*)\/?$/ } component={ Article } />
        <Route name="article" pattern={ /^\/(?<locale>(en|he|fa))\/news\/(?<rootCategory>[^/]+)\/((?<subCategory>[^/]+)\/)?(?<article>([0-9]{10}|[0-9]{6}-[0-9]{6}|artc-).*)\/?$/ } component={ Article } />
      </ErrorBoundary>
    </Wrapper>
  </div>

// App :: React.Props -> React.Component
export default ({ store, location, onMount = () => {} }) => {
  useEffect(() => {
    onMount()

    return () => {}
  }, [])

  return (
    <React.StrictMode>
      <Provider store={ store }>
        <Router location={ location }>
          { isWebView(location) ? <WebviewTree /> : <AppTree /> }
        </Router>
      </Provider>
    </React.StrictMode>
  )
}
